import { useState, useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Badge,
  Button,
  Separator,
  useToast,
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@repo/ui";
import {
  Phone,
  MapPin,
  Pencil,
  Trash,
  Pill,
  Bell,
  Check,
  Calendar,
  User,
  House,
} from "@phosphor-icons/react";
import { Loader2 } from "lucide-react";
import { format, parseISO } from "date-fns";
import { PermissionGate } from "@/components/security";
import { getPatient, deletePatient } from "@/lib/api/patients";
import { ApiClientError } from "@/lib/api/client";
import { useApproval } from "@/context/approval-context";
import { handlePermissionError } from "@/lib/utils/handle-permission-error";
import type {
  Patient,
  PatientDetail,
  PatientLinkedDispense,
  PatientLinkedReminder,
} from "@/lib/types/patients";

interface PatientDetailDrawerProps {
  patient: Patient | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit?: (patient: Patient) => void;
  onDeleted?: () => void;
}

function formatFullName(patient: Patient): string {
  const parts = [patient.first_name, patient.middle_name, patient.last_name];
  return parts.filter(Boolean).join(" ");
}

function getInitials(patient: Patient): string {
  const first = patient.first_name?.[0] ?? "";
  const last = patient.last_name?.[0] ?? "";
  return (first + last).toUpperCase() || "?";
}

function formatAddress(patient: Patient): string {
  const parts = [
    patient.street,
    patient.street_2,
    patient.city,
    patient.state,
    patient.zip,
  ];
  return parts.filter(Boolean).join(", ");
}

function formatDate(dateStr: string): string {
  try {
    return format(parseISO(dateStr), "MMM d, yyyy");
  } catch {
    return dateStr;
  }
}

function getPriorityColor(priority: string): string {
  switch (priority) {
    case "urgent":
      return "bg-red-500";
    case "high":
      return "bg-orange-500";
    case "medium":
      return "bg-amber-500";
    default:
      return "bg-blue-400";
  }
}

// ── Section label ────────────────────────────────────────────────────────────

function SectionLabel({
  icon: Icon,
  label,
}: {
  icon: typeof Pill;
  label: string;
}) {
  return (
    <div className="flex items-center gap-1.5 mb-2">
      <Icon size={13} className="text-muted-foreground" />
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
    </div>
  );
}

// ── Main Component ──────────────────────────────────────────────────────────

export function PatientDetailDrawer({
  patient,
  open,
  onOpenChange,
  onEdit,
  onDeleted,
}: PatientDetailDrawerProps) {
  const { toast } = useToast();
  const { requestApproval } = useApproval();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<PatientDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleDispenseClick = (id: string) => {
    onOpenChange(false);
    navigate({ to: "/dispenses", search: { id } });
  };

  const handleReminderClick = (id: string) => {
    onOpenChange(false);
    navigate({ to: "/reminders", search: { id } });
  };

  useEffect(() => {
    if (!open || !patient?.id) {
      setDetail(null);
      return;
    }

    setIsLoading(true);
    getPatient(patient.id)
      .then((res) => {
        setDetail(res.data.patient);
      })
      .catch(() => {
        setDetail(null);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [open, patient?.id]);

  const fullName = patient ? formatFullName(patient) : "";
  const initials = patient ? getInitials(patient) : "";
  const address = patient ? formatAddress(patient) : "";

  const hasLinkedRecords = patient
    ? patient.dispense_count + patient.reminder_count > 0
    : false;

  const handleDeleteClick = () => {
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!patient) return;

    setShowDeleteDialog(false);
    setIsDeleting(true);
    try {
      await deletePatient(patient.id);
      toast({
        title: "Patient Deleted",
        description: `${fullName} has been removed.`,
        variant: "success",
      });
      onOpenChange(false);
      onDeleted?.();
    } catch (error) {
      if (
        error instanceof ApiClientError &&
        handlePermissionError(error, {
          requestApproval,
          toast,
          retryFn: async (token) => {
            await deletePatient(patient.id, { approvalToken: token });
            toast({
              title: "Patient Deleted",
              description: `${fullName} has been removed.`,
              variant: "success",
            });
            onOpenChange(false);
            onDeleted?.();
          },
          actionDescription: "Delete patient",
        })
      )
        return;
      toast({
        title: "Error",
        description:
          error instanceof Error ? error.message : "Failed to delete patient",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const dispenseCount = detail?.dispenses.length ?? 0;
  const reminderCount = detail?.reminders.length ?? 0;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-xl flex flex-col p-0 overflow-hidden"
          data-testid="patient-detail-drawer"
        >
          <SheetDescription className="sr-only">
            Patient detail view for {fullName}
          </SheetDescription>

          {isLoading ? (
            <div className="flex items-center justify-center flex-1">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : patient ? (
            <div className="flex flex-col flex-1 overflow-hidden">
              {/* Header */}
              <div className="px-6 pt-6 pb-4">
                <SheetHeader className="mb-0">
                  <div className="flex items-start gap-4">
                    {/* Avatar */}
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary text-lg font-semibold">
                      {initials}
                    </div>

                    <div className="flex-1 min-w-0">
                      <SheetTitle className="text-xl leading-tight">
                        {fullName}
                      </SheetTitle>

                      {/* Contact details */}
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
                        {patient.date_of_birth && (
                          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                            <Calendar size={13} />
                            <span>
                              {format(
                                parseISO(patient.date_of_birth),
                                "MM/dd/yyyy"
                              )}
                            </span>
                          </div>
                        )}
                        {patient.phone_number && (
                          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                            <Phone size={13} />
                            <span>{patient.phone_number}</span>
                          </div>
                        )}
                        {address && (
                          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                            <MapPin size={13} />
                            <span className="truncate">{address}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </SheetHeader>

                {/* Stats + Actions row */}
                <div className="flex items-center justify-between mt-4">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1.5 rounded-md bg-muted/50 px-2.5 py-1.5">
                      <Pill size={13} className="text-primary" />
                      <span className="text-sm font-semibold tabular-nums">
                        {dispenseCount}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        dispense{dispenseCount !== 1 ? "s" : ""}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 rounded-md bg-muted/50 px-2.5 py-1.5">
                      <Bell size={13} className="text-secondary" />
                      <span className="text-sm font-semibold tabular-nums">
                        {reminderCount}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        reminder{reminderCount !== 1 ? "s" : ""}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5">
                    <PermissionGate permission="patients:edit">
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1.5 h-8"
                        onClick={() => onEdit?.(patient)}
                      >
                        <Pencil size={13} />
                        Edit
                      </Button>
                    </PermissionGate>
                    <PermissionGate permission="patients:delete">
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1.5 h-8 text-destructive hover:text-destructive"
                        onClick={handleDeleteClick}
                        disabled={isDeleting}
                      >
                        {isDeleting ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Trash size={13} />
                        )}
                        Delete
                      </Button>
                    </PermissionGate>
                  </div>
                </div>

                {/* Quick actions */}
                <div className="flex items-center gap-2 mt-3">
                  <PermissionGate permission="dispenses:create">
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5 h-8 flex-1"
                      onClick={() => {
                        onOpenChange(false);
                        navigate({
                          to: "/dispenses",
                          search: {
                            action: "new",
                            patientId: patient.id,
                            patientName: fullName,
                          },
                        });
                      }}
                    >
                      <Pill size={13} />
                      Log Dispense
                    </Button>
                  </PermissionGate>
                  <PermissionGate permission="reminders:create">
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5 h-8 flex-1"
                      onClick={() => {
                        onOpenChange(false);
                        navigate({
                          to: "/reminders",
                          search: {
                            action: "create",
                            patientId: patient.id,
                            patientName: fullName,
                          },
                        });
                      }}
                    >
                      <Bell size={13} />
                      Add Reminder
                    </Button>
                  </PermissionGate>
                </div>
              </div>

              <Separator />

              {/* Tabs — scrollable content */}
              <Tabs
                defaultValue="dispenses"
                className="flex flex-col flex-1 overflow-hidden"
              >
                <div className="px-6 pt-3">
                  <TabsList className="w-full">
                    <TabsTrigger value="dispenses" className="flex-1 gap-1.5">
                      <Pill size={14} />
                      Dispenses
                      {dispenseCount > 0 && (
                        <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-soft px-1 text-[10px] font-semibold text-primary">
                          {dispenseCount}
                        </span>
                      )}
                    </TabsTrigger>
                    <TabsTrigger value="reminders" className="flex-1 gap-1.5">
                      <Bell size={14} />
                      Reminders
                      {reminderCount > 0 && (
                        <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-secondary-soft px-1 text-[10px] font-semibold text-secondary">
                          {reminderCount}
                        </span>
                      )}
                    </TabsTrigger>
                    <TabsTrigger value="info" className="flex-1 gap-1.5">
                      <User size={14} />
                      Info
                    </TabsTrigger>
                  </TabsList>
                </div>

                <div className="flex-1 overflow-y-auto px-6 pb-6">
                  <TabsContent value="dispenses" className="mt-4">
                    <DispensesTab
                      dispenses={detail?.dispenses ?? []}
                      onDispenseClick={handleDispenseClick}
                    />
                  </TabsContent>

                  <TabsContent value="reminders" className="mt-4">
                    <RemindersTab
                      reminders={detail?.reminders ?? []}
                      onReminderClick={handleReminderClick}
                    />
                  </TabsContent>

                  <TabsContent value="info" className="mt-4">
                    <InfoTab patient={patient} />
                  </TabsContent>
                </div>
              </Tabs>
            </div>
          ) : null}
        </SheetContent>
      </Sheet>

      {patient && (
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Patient</AlertDialogTitle>
              <AlertDialogDescription>
                {hasLinkedRecords ? (
                  <>
                    <span className="font-semibold text-foreground">
                      &quot;{fullName}&quot;
                    </span>{" "}
                    cannot be deleted because they have linked records:
                  </>
                ) : (
                  <>
                    Are you sure you want to delete{" "}
                    <span className="font-semibold text-foreground">
                      &quot;{fullName}&quot;
                    </span>
                    ? This action cannot be undone.
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>

            {hasLinkedRecords && (
              <div className="flex items-center gap-4 rounded-lg border bg-muted/40 px-4 py-3">
                {patient.dispense_count > 0 && (
                  <div className="flex items-center gap-1.5">
                    <Pill size={14} className="text-primary" />
                    <span className="text-sm font-medium tabular-nums">
                      {patient.dispense_count}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      dispense{patient.dispense_count !== 1 ? "s" : ""}
                    </span>
                  </div>
                )}
                {patient.reminder_count > 0 && (
                  <div className="flex items-center gap-1.5">
                    <Bell size={14} className="text-secondary" />
                    <span className="text-sm font-medium tabular-nums">
                      {patient.reminder_count}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      reminder{patient.reminder_count !== 1 ? "s" : ""}
                    </span>
                  </div>
                )}
              </div>
            )}

            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              {hasLinkedRecords ? (
                <AlertDialogAction
                  disabled
                  className="bg-destructive text-white opacity-50 cursor-not-allowed hover:bg-destructive"
                >
                  Delete
                </AlertDialogAction>
              ) : (
                <AlertDialogAction
                  onClick={handleConfirmDelete}
                  className="bg-destructive text-white hover:bg-destructive/90"
                >
                  Delete
                </AlertDialogAction>
              )}
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
}

// ── Dispenses Tab ────────────────────────────────────────────────────────────

function DispensesTab({
  dispenses,
  onDispenseClick,
}: {
  dispenses: PatientLinkedDispense[];
  onDispenseClick: (id: string) => void;
}) {
  if (dispenses.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-14 text-center">
        <div className="rounded-full bg-muted p-3 mb-3">
          <Pill size={20} className="text-muted-foreground" />
        </div>
        <p className="text-sm font-medium text-muted-foreground">
          No dispenses yet
        </p>
        <p className="text-xs text-muted-foreground mt-1 max-w-[200px]">
          Dispenses linked to this patient will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <SectionLabel icon={Pill} label="Dispense History" />
      <div className="space-y-1.5">
        {dispenses.map((d) => (
          <div
            key={d.id}
            role="button"
            tabIndex={0}
            onClick={() => onDispenseClick(d.id)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onDispenseClick(d.id);
              }
            }}
            className="flex items-center gap-3 rounded-lg border border-border/60 bg-surface p-3 hover:bg-muted/20 transition-colors cursor-pointer"
          >
            {/* Icon */}
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/8">
              <Pill size={14} className="text-primary" />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium leading-tight truncate">
                {d.item_name ?? d.ndc}
              </p>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-xs text-muted-foreground">
                  {formatDate(d.date)}
                </span>
                {d.created_by_name && (
                  <>
                    <span className="text-xs text-muted-foreground">·</span>
                    <span className="text-xs text-muted-foreground truncate">
                      {d.created_by_name}
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* Right side — RX + Qty */}
            <div className="flex flex-col items-end shrink-0 gap-0.5">
              <span className="text-xs font-mono text-muted-foreground">
                RX {d.rx_number}
              </span>
              <span className="text-sm font-semibold tabular-nums">
                Qty {d.quantity_dispensed}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Reminders Tab ────────────────────────────────────────────────────────────

function RemindersTab({
  reminders,
  onReminderClick,
}: {
  reminders: PatientLinkedReminder[];
  onReminderClick: (id: string) => void;
}) {
  if (reminders.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-14 text-center">
        <div className="rounded-full bg-muted p-3 mb-3">
          <Bell size={20} className="text-muted-foreground" />
        </div>
        <p className="text-sm font-medium text-muted-foreground">
          No reminders yet
        </p>
        <p className="text-xs text-muted-foreground mt-1 max-w-[200px]">
          Reminders linked to this patient will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <SectionLabel icon={Bell} label="Linked Reminders" />
      <div className="space-y-1.5">
        {reminders.map((r) => (
          <div
            key={r.id}
            role="button"
            tabIndex={0}
            onClick={() => onReminderClick(r.id)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onReminderClick(r.id);
              }
            }}
            className="flex items-start gap-3 rounded-lg border border-border/60 bg-surface p-3 hover:bg-muted/20 transition-colors cursor-pointer"
          >
            {/* Completion indicator */}
            <div
              className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
                r.is_completed
                  ? "bg-primary/10 text-primary"
                  : "border-2 border-border"
              }`}
            >
              {r.is_completed && <Check size={11} weight="bold" />}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span
                  className={`text-sm font-medium leading-tight ${
                    r.is_completed
                      ? "text-muted-foreground line-through"
                      : "text-foreground"
                  }`}
                >
                  {r.title}
                </span>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <Calendar size={11} className="text-muted-foreground" />
                <span className="text-xs text-muted-foreground">
                  {formatDate(r.due_date)}
                  {r.due_time ? ` at ${r.due_time.slice(0, 5)}` : ""}
                </span>
              </div>
            </div>

            {/* Priority badge */}
            <Badge
              variant="outline"
              className="gap-1 text-[10px] h-5 px-1.5 shrink-0"
            >
              <span
                className={`inline-block h-1.5 w-1.5 rounded-full ${getPriorityColor(r.priority)}`}
              />
              {r.priority}
            </Badge>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Info Tab ─────────────────────────────────────────────────────────────────

function InfoTab({ patient }: { patient: Patient }) {
  const address = formatAddress(patient);

  return (
    <div className="space-y-5">
      {/* Name */}
      <div>
        <SectionLabel icon={User} label="Name" />
        <div className="grid grid-cols-3 gap-3">
          <InfoField label="First" value={patient.first_name} />
          <InfoField label="Middle" value={patient.middle_name} />
          <InfoField label="Last" value={patient.last_name} />
        </div>
      </div>

      <Separator />

      {/* Date of Birth */}
      <div>
        <SectionLabel icon={Calendar} label="Date of Birth" />
        <InfoField
          label="DOB"
          value={
            patient.date_of_birth
              ? format(parseISO(patient.date_of_birth), "MM/dd/yyyy")
              : null
          }
        />
      </div>

      <Separator />

      {/* Contact */}
      <div>
        <SectionLabel icon={Phone} label="Contact" />
        <div className="grid grid-cols-2 gap-3">
          <InfoField label="Phone" value={patient.phone_number} icon={Phone} />
        </div>
      </div>

      <Separator />

      {/* Address */}
      <div>
        <SectionLabel icon={House} label="Address" />
        {address ? (
          <div className="grid grid-cols-2 gap-3">
            <InfoField label="Street" value={patient.street} />
            <InfoField label="Apt/Suite" value={patient.street_2} />
            <InfoField label="City" value={patient.city} />
            <InfoField label="State" value={patient.state} />
            <InfoField label="ZIP" value={patient.zip} />
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No address on file</p>
        )}
      </div>

      <Separator />

      {/* Dates */}
      <div>
        <SectionLabel icon={Calendar} label="Record" />
        <div className="grid grid-cols-2 gap-3">
          <InfoField label="Added" value={formatDate(patient.created_at)} />
          <InfoField label="Updated" value={formatDate(patient.updated_at)} />
        </div>
      </div>
    </div>
  );
}

function InfoField({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: string | null | undefined;
  icon?: typeof Phone;
}) {
  return (
    <div>
      <p className="text-[11px] font-medium text-muted-foreground mb-0.5">
        {label}
      </p>
      <div className="flex items-center gap-1.5">
        {Icon && value && <Icon size={13} className="text-muted-foreground" />}
        <p className="text-sm text-foreground">{value || "—"}</p>
      </div>
    </div>
  );
}
